import React from 'react';
import { motion } from 'framer-motion';
import { BeakerIcon } from '@heroicons/react/24/outline';
import { TeaserDemoProps } from '../../config/teaserContent';
import { createOptimizedTeaserDemo, useTeaserDemoState } from './baseTeaserDemo';
import { mockStrategyHealthData } from '../../data/teaserMockData';

interface StrategyXRayDemoProps extends TeaserDemoProps {
  strategy?: string;
  healthScore?: number;
  components?: typeof mockStrategyHealthData.components;
  metrics?: typeof mockStrategyHealthData.metrics;
}

const StrategyXRayDemoComponent: React.FC<StrategyXRayDemoProps> = ({ 
  className, 
  aspectRatio = 'video',
  strategy = mockStrategyHealthData.strategy,
  healthScore = mockStrategyHealthData.healthScore,
  components = mockStrategyHealthData.components,
  metrics = mockStrategyHealthData.metrics
}) => {
  const { containerClass } = useTeaserDemoState(aspectRatio);

  const getScoreColor = (score: number) => {
    if (score >= 85) return 'text-green-400';
    if (score >= 70) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getBarColor = (score: number) => {
    if (score >= 85) return 'from-green-400 to-emerald-400';
    if (score >= 70) return 'from-yellow-400 to-orange-400';
    return 'from-red-400 to-pink-400';
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={`relative p-4 h-full flex flex-col justify-between ${containerClass} ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <motion.div
          animate={{ 
            boxShadow: [
              '0 0 10px rgba(59, 130, 246, 0.3)',
              '0 0 20px rgba(59, 130, 246, 0.6)',
              '0 0 10px rgba(59, 130, 246, 0.3)'
            ]
          }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center"
        >
          <BeakerIcon className="w-4 h-4 text-white" />
        </motion.div>
        <div>
          <h4 className="text-white font-semibold text-sm">Strategy X-Ray</h4>
          <p className="text-gray-300 text-xs">{strategy}</p>
        </div>
        <div className="ml-auto">
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className={`text-2xl font-bold ${getScoreColor(healthScore)}`}
          >
            {healthScore}
          </motion.div>
          <p className="text-xs text-gray-400 text-center">Health</p>
        </div>
      </div>

      {/* Component Scores */}
      <div className="space-y-2 mb-4">
        {Object.entries(components).map(([key, component], index) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.2, duration: 0.5 }}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-white text-sm font-medium">{component.label}</span>
              <span className={`text-sm font-bold ${getScoreColor(component.score)}`}>
                {component.score}
              </span>
            </div>
            <div className="bg-black/30 rounded-full h-2 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(component.score / 100) * 100}%` }}
                transition={{ delay: index * 0.2 + 0.5, duration: 1.5, ease: "easeOut" }}
                className={`h-full bg-gradient-to-r ${getBarColor(component.score)} rounded-full`}
              />
            </div>
          </motion.div>
        ))}
      </div>

      {/* Key Metrics */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.5 }}
        className="grid grid-cols-2 gap-2 text-center"
      >
        <div className="bg-white/5 rounded-lg p-2 backdrop-blur-sm">
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-green-400 font-bold text-sm"
          >
            {metrics.winRate}%
          </motion.div>
          <p className="text-xs text-gray-400">Win Rate</p>
        </div>
        <div className="bg-white/5 rounded-lg p-2 backdrop-blur-sm">
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
            className="text-blue-400 font-bold text-sm"
          >
            {metrics.sharpeRatio}
          </motion.div>
          <p className="text-xs text-gray-400">Sharpe</p>
        </div>
      </motion.div>

      {/* Analysis Badge */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.5 }}
        className="mt-3 text-center"
      >
        <motion.p
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-purple-300 text-xs font-medium"
        >
          🔬 Deep performance analysis
        </motion.p>
      </motion.div>
    </motion.div>
  );
};

export const StrategyXRayDemo = createOptimizedTeaserDemo(
  StrategyXRayDemoComponent,
  'StrategyXRayDemo'
);